(() => {
  const MEASUREMENT_ID = "G-EP92WD45GK";
  const DIY_PATH = "/pages/design-your-bracelet";
  const SESSION_ID_KEY = "tce_diy_session_id";
  const STARTED_KEY = "tce_diy_builder_started";
  const SOURCE_ARTICLE_KEY = "tce_diy_source_article";
  const EVENT_NAMES = new Set([
    "diy_builder_view",
    "diy_builder_start",
    "diy_bead_select",
    "diy_size_select",
    "diy_length_change",
    "diy_accessory_select",
    "diy_design_reorder",
    "diy_design_clear",
    "diy_cart_submit_click",
    "diy_cart_submit_result",
    "diy_support_link_click",
    "diy_article_to_builder_click"
  ]);
  const DORMANT_EVENTS = new Set(["diy_length_change"]);
  const script = document.currentScript;
  const context = {
    pageType: script?.dataset.pageType || "",
    articleId: script?.dataset.articleId || "",
    articleUrl: script?.dataset.articleUrl || "",
    articlePrimaryQuery: script?.dataset.articlePrimaryQuery || "unknown"
  };
  const debugMode = new URLSearchParams(window.location.search).get("tce_diy_debug") === "1";
  let analyticsAllowed = false;
  let builderViewSent = false;
  let builderViewRetryCount = 0;
  let builderViewRetryTimer = null;
  let builderObserver = null;

  function privacyApi() {
    return window.Shopify?.customerPrivacy;
  }

  function syncConsent() {
    analyticsAllowed = privacyApi()?.analyticsProcessingAllowed?.() === true;
    if (analyticsAllowed) tryBuilderView();
  }

  function initConsent() {
    if (privacyApi()) {
      syncConsent();
      return;
    }
    if (typeof window.Shopify?.loadFeatures !== "function") return;
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      (error) => {
        if (!error) syncConsent();
      }
    );
  }

  function sessionGet(key) {
    if (!analyticsAllowed) return "";
    try {
      return window.sessionStorage.getItem(key) || "";
    } catch (_error) {
      return "";
    }
  }

  function sessionSet(key, value) {
    if (!analyticsAllowed) return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      // Analytics still works without session persistence.
    }
  }

  function diySessionId() {
    let value = sessionGet(SESSION_ID_KEY);
    if (value) return value;
    value = window.crypto?.randomUUID?.() || `diy-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionSet(SESSION_ID_KEY, value);
    return value;
  }

  function safeUrl(value) {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (_error) {
      return "";
    }
  }

  function safePath(value) {
    if (!value) return "";
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (_error) {
      return "";
    }
  }

  function sourceArticle() {
    const value = sessionGet(SOURCE_ARTICLE_KEY);
    if (!value) return {};
    try {
      return JSON.parse(value);
    } catch (_error) {
      return {};
    }
  }

  function trafficContext(source) {
    if (source.url || safePath(document.referrer).startsWith("/blogs/")) return "blog_referral";
    if (!document.referrer) return "direct";
    try {
      const host = new URL(document.referrer).hostname;
      if (/google\.|bing\.|yahoo\.|duckduckgo\.|ecosia\./i.test(host)) return "organic";
      return host === window.location.hostname ? "other" : "other";
    } catch (_error) {
      return "unknown";
    }
  }

  function globalParameters() {
    const source = sourceArticle();
    return {
      page_path: window.location.pathname,
      page_location: safeUrl(window.location.href),
      page_title: document.title,
      diy_session_id: diySessionId(),
      traffic_context: trafficContext(source),
      referrer_path: safePath(document.referrer),
      source_article_url: source.url || "",
      source_article_id: source.id || ""
    };
  }

  function cleanParameters(parameters) {
    return Object.fromEntries(
      Object.entries(parameters).map(([key, value]) => [key, value ?? ""])
    );
  }

  function emit(eventName, parameters = {}) {
    if (!EVENT_NAMES.has(eventName) || DORMANT_EVENTS.has(eventName)) return false;
    if (!analyticsAllowed || typeof window.gtag !== "function") return false;
    const payload = cleanParameters({
      ...globalParameters(),
      ...parameters,
      send_to: MEASUREMENT_ID,
      event_transport: "beacon",
      ...(debugMode ? { debug_mode: true } : {})
    });
    window.gtag("event", eventName, payload);
    return true;
  }

  function emitBuilderStart(startAction) {
    if (sessionGet(STARTED_KEY)) return false;
    const sent = emit("diy_builder_start", { start_action: startAction || "unknown" });
    if (sent) sessionSet(STARTED_KEY, "1");
    return sent;
  }

  function builderIsVisible(root) {
    const rect = root.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function tryBuilderView() {
    if (builderViewSent || !analyticsAllowed || window.location.pathname !== DIY_PATH) return;
    const root = document.querySelector("[data-minestone-diy]");
    if (!root || (!window.location.hash.includes("design-your-bracelet") && !builderIsVisible(root))) return;
    if (!root.dataset.componentBindingTotal && builderViewRetryCount < 40) {
      builderViewRetryCount += 1;
      window.clearTimeout(builderViewRetryTimer);
      builderViewRetryTimer = window.setTimeout(tryBuilderView, 50);
      return;
    }
    builderViewSent = emit("diy_builder_view", {
      builder_anchor_visible: true,
      catalog_loaded: root.dataset.componentBindingTotal !== "" && root.dataset.componentBindingMissing === "0"
    });
    if (builderViewSent) {
      window.clearTimeout(builderViewRetryTimer);
      builderObserver?.disconnect();
    }
  }

  function observeBuilder() {
    if (window.location.pathname !== DIY_PATH) return;
    const root = document.querySelector("[data-minestone-diy]");
    if (!root) return;
    if ("IntersectionObserver" in window) {
      builderObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) tryBuilderView();
      }, { threshold: 0.15 });
      builderObserver.observe(root);
    }
    tryBuilderView();
  }

  function storeArticleContext() {
    const articleContext = {
      id: context.articleId || "unknown",
      url: safeUrl(context.articleUrl || window.location.href),
      primary_query: context.articlePrimaryQuery || "unknown"
    };
    sessionSet(SOURCE_ARTICLE_KEY, JSON.stringify(articleContext));
    return articleContext;
  }

  function handleTrackedLink(event) {
    const link = event.target.closest("a[href]");
    if (!link) return;
    const destination = new URL(link.href, window.location.origin);
    if (window.location.pathname === DIY_PATH && link.matches("[data-diy-support-link]")) {
      emit("diy_support_link_click", {
        destination_url: safeUrl(destination.href),
        destination_type: link.dataset.destinationType || "support_article",
        anchor_text: link.textContent.trim(),
        link_context: link.dataset.linkContext || "diy_support"
      });
      return;
    }
    if (context.pageType === "article" && destination.pathname === DIY_PATH) {
      const articleContext = storeArticleContext();
      emit("diy_article_to_builder_click", {
        article_id: articleContext.id,
        article_url: articleContext.url,
        article_primary_query: articleContext.primary_query,
        destination_url: safeUrl(destination.href),
        anchor_text: link.textContent.trim()
      });
    }
  }

  window.TCEDIYAnalytics = Object.freeze({
    emit,
    emitBuilderStart,
    allowedEvents: Object.freeze([...EVENT_NAMES]),
    dormantEvents: Object.freeze([...DORMANT_EVENTS]),
    measurementId: MEASUREMENT_ID,
    consentAllowed: () => analyticsAllowed,
    debugMode
  });

  document.addEventListener("visitorConsentCollected", syncConsent);
  document.addEventListener("click", handleTrackedLink);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observeBuilder, { once: true });
  } else {
    observeBuilder();
  }
  initConsent();
})();
